import { apiClient } from './apiClient';
import rpcTasks from './rpc/rpcTasks';

/**
 * Internal helper for RPC reminders calls
 */
async function rpcReminders<T>(company: string, action: string, payload: any): Promise<T> {
  const { data } = await apiClient.post(`/api/${company}/rpc/reminders/${action}`, payload, {
    withCredentials: true,
  });
  // If backend returns { ok, data }, unwrap it here
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  // If backend returns { ok: true, reminder } or { ok: true, reminders }, return the data directly
  if ((data as any)?.ok) {
    // For responses like { ok: true, reminder } or { ok: true, reminders }
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * Get reminder by ID
 */
export async function getReminder(company: string, reminderId: string) {
  const result = await rpcReminders<{ reminder: any }>(company, 'get-reminder', { id: reminderId });
  return result;
}

/**
 * Save reminder as task
 */
export async function saveReminderAsTask(company: string, reminderId: string) {
  const result = await rpcReminders<{ reminder: any; task: any; created: boolean }>(
    company,
    'save-as-task',
    { id: reminderId }
  );
  return result;
}

/**
 * Update reminder
 */
export async function updateReminder(company: string, reminderId: string, payload: any) {
  const result = await rpcReminders<{ reminder: any }>(company, 'update', {
    id: reminderId,
    ...payload,
  });
  return result;
}

/**
 * Get reminders for a client
 * Now uses RPC: /api/:company/rpc/clients/get-client-reminders
 */
export async function getClientReminders(company: string, clientId: string) {
  const { data } = await rpcTasks.getClientReminders(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client reminders');
}

/**
 * Get all reminders for the current user (created by them)
 */
export async function getUserReminders(company: string) {
  const result = await rpcReminders<{ reminders: any[] }>(company, 'list-user-reminders', {});
  return result;
}

/**
 * Get reminder by note text (for note reminders)
 */
export async function getReminderByNote(company: string, noteText: string, clientId: string) {
  const result = await rpcReminders<{ reminder: any }>(company, 'get-reminder-by-note', { 
    noteText, 
    clientId 
  });
  return result;
}

/**
 * Complete reminder (mark as completed and optionally update task status)
 */
export async function completeReminder(company: string, reminderId: string, updateTaskStatus: boolean = true) {
  const result = await rpcReminders<{ reminder: any; message: string }>(company, 'complete', {
    id: reminderId,
    updateTaskStatus
  });
  return result;
}

